import type { EndpointMap } from '../common/endpoint';
import type {
  ImpersonateRequest,
  LegacyExchangeRequest,
  LoginRequest,
  RefreshSessionRequest,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  SendLoginCodeRequest,
} from './auth.request';
import type { GetAppStatusResponse, SessionResponse, SessionUser } from './auth.response';

/**
 * 인증 · 세션 · 앱 상태. 경로는 `/api` prefix 를 뺀 것이다.
 * 리프레시 토큰은 httpOnly 쿠키(`Path=/api/auth`)로만 오간다.
 */
export const authEndpoints = {
  getAppStatus: { method: 'GET', path: '/app/status', res: {} as GetAppStatusResponse },
  sendLoginCode: {
    method: 'POST',
    path: '/auth/login/code',
    req: {} as SendLoginCodeRequest,
    res: undefined as void,
    errors: ['BAD_REQUEST_INVALID_PARAMETERS', 'UNAUTHORIZED_LOGIN_FAILED', 'TOO_MANY_REQUESTS'],
  },
  login: {
    method: 'POST',
    path: '/auth/login',
    req: {} as LoginRequest,
    res: {} as SessionResponse,
    errors: [
      'BAD_REQUEST_INVALID_PARAMETERS',
      'UNAUTHORIZED_LOGIN_FAILED',
      'UNAUTHORIZED_LOGIN_CODE_INVALID',
      'UNAUTHORIZED_LOGIN_CODE_EXPIRED',
      'TOO_MANY_REQUESTS',
    ],
  },
  refreshSession: {
    method: 'POST',
    path: '/auth/refresh',
    req: {} as RefreshSessionRequest,
    res: {} as SessionResponse,
    errors: [
      'BAD_REQUEST_INVALID_PARAMETERS',
      'UNAUTHORIZED_SESSION_INVALID',
      'FORBIDDEN_ROLE_NOT_GRANTED',
      'TOO_MANY_REQUESTS',
    ],
  },
  logout: { method: 'POST', path: '/auth/logout', res: undefined as void },
  /** 구 서버 로그인 이어받기(DD-88, 2027-01-31 까지). 성공하면 리프레시 쿠키 발급 · 구 토큰은 다시 못 쓴다 */
  legacyExchange: {
    method: 'POST',
    path: '/auth/legacy-exchange',
    req: {} as LegacyExchangeRequest,
    res: {} as SessionResponse,
    errors: [
      'BAD_REQUEST_INVALID_PARAMETERS',
      'UNAUTHORIZED_LEGACY_TOKEN_REJECTED',
      'TOO_MANY_REQUESTS',
    ],
  },
  /**
   * 대리 로그인 시작(D3 · 관리자). 대리 중에 다시 부르면 액세스 토큰의 역할이 대상 역할(lp · portfolio)이라
   * 전역 가드가 역할 FORBIDDEN 으로 막는다. 대상이 없거나 로그인할 수 없는 회원이면 NOT_FOUND_MEMBER
   */
  impersonate: {
    method: 'POST',
    path: '/auth/impersonate',
    req: {} as ImpersonateRequest,
    res: {} as SessionResponse,
    errors: [
      'BAD_REQUEST_INVALID_PARAMETERS',
      'FORBIDDEN',
      'FORBIDDEN_IMPERSONATION_NOT_ALLOWED',
      'NOT_FOUND_MEMBER',
    ],
  },
  requestPasswordReset: {
    method: 'POST',
    path: '/auth/password-reset/request',
    req: {} as RequestPasswordResetRequest,
    res: undefined as void,
    errors: [
      'BAD_REQUEST_INVALID_PARAMETERS',
      'NOT_FOUND_PASSWORD_RESET_EMAIL',
      'TOO_MANY_REQUESTS',
    ],
  },
  resetPassword: {
    method: 'POST',
    path: '/auth/password-reset',
    req: {} as ResetPasswordRequest,
    res: undefined as void,
    errors: [
      'BAD_REQUEST_INVALID_PARAMETERS',
      'UNAUTHORIZED_PASSWORD_RESET_INVALID',
      'TOO_MANY_REQUESTS',
    ],
  },
  getMe: { method: 'GET', path: '/me', res: {} as SessionUser, errors: ['UNAUTHORIZED'] },
} as const satisfies EndpointMap;
